import MarkerUtils from "@/helpers/markerUtils"
import LiikenneUtils from "@/helpers/liikenneUtils"
import AreaUtils from "@/helpers/areaUtils"
import ApiDataModel, { GeometryData } from "@/models/apidatamodel"
import DynamicRightSlotData from "@/models/dynamicrighslotdata"
import TableData from "@/models/tabledata"
import L from "leaflet"
import moment from "moment"
import { AreaTypes } from "@/helpers/enums"
import LegendLimitsUtils from "@/helpers/legendLimitsUtils"
import utils from "../utils/utils"
import { store } from '@/store/store'
import i18n from '@/i18n'

const t = i18n.global.t

export default class TieliikenneService {
    MarkerUtils = new MarkerUtils(store.getters.contentServiceUrl, store.getters.contentServiceTimeStamp)
    LegendLimitsUtils = new LegendLimitsUtils()
    LiikenneUtils = new LiikenneUtils()
    AreaUtils = new AreaUtils()

    myonnetytTaksiluvatLegendLimits = this.LegendLimitsUtils.getLegendLimits("MyonnetytTaksiluvat")
    voimassaOlevatAjokortitLegendLimits = this.LegendLimitsUtils.getLegendLimits("VoimassaOlevatAjokortit")

    /** Haetun datan käsittelyä: Lupa- ja Rekisteröintitoimipaikat
     eri värisiä markereita riippuen Tyypistä / legenda näytettävä / tooltip tyypin mukaan
     rightslotissa näytetään koska tiedot on päivitetty sekä kartalla näkyvien toimipisteiden määrä, käsitellään map.vue ja piirron lopuksi
     TODO: Kartan näkyvä alue tieto vielä harkinnassa*/
    async handleDataLupaJaRekisterointitoimipaikat(data): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()
            let lupaToimipaikkaUpdatedText = ""
            let rekisterointiToimipaikkaUpdatedText = ""

            data.forEach((value) => {
                const markerOptions = {
                    id: value.id,
                    type: value.tyyppi,
                    riseOnHover: true,
                    icon: L.icon({
                        iconUrl: this.getToimipaikkaTyyppiIcon(value.tyyppi),
                        iconAnchor: [12, 40],
                        iconSize: [24, 40],
                        popupAnchor: [0, -30]
                    })
                }
                value.koordinaatit.markerOptions = markerOptions

                let popup = "<p style='margin: 0px;'><b>" + value.nimi + "</b></p>"
                popup += "<div>" + value.katuosoite + "<br/>"
                    + value.postinro + " " + value.postitoimipaikka?.toUpperCase() + "<br/>"

                if (value.tyyppi.toLowerCase() === "ajokorttijalupapalvelupiste") {
                    popup += t("App.Tieliikenne.Popup.PalvelutHinnastoJaAukioloajat") + "<br/>"
                    popup += "<a class='popuplink' target='_blank' href='" + this.LiikenneUtils.getUrl(value) + "'> " + this.createPopUpLinkAriaLabel(value.nimi)
                        + "<img class='iframe-external-icon' src='" + this.getExternalLink() + "' alt='"
                        + this.getExternalLinkAlt() + "'>" + utils.getLinkOpensNewTabSpan() + "</a>"
                }
                else {
                    popup += "<b>" + t("App.TieLiikenne.Popup.Rekisterointipalvelut") + ":</b>"

                    // muotoillaan pisteellä eroteltu infotext listaksi (valitun kielen mukaan haetaan oikea teksti)
                    const infoText = this.LiikenneUtils.getInfoText(value)
                    popup += "<ul> "
                    infoText.forEach(function (item) {
                        popup += "<li>" + item + "</li>"
                    })
                    popup += "</ul>"
                }
                popup += "</div>"
                value.popup = popup

                if (lupaToimipaikkaUpdatedText.length === 0 && value.tyyppi.toLowerCase() === "ajokorttijalupapalvelupiste") {
                    lupaToimipaikkaUpdatedText = value.julkaisuaika
                }
                if (rekisterointiToimipaikkaUpdatedText.length === 0 && value.tyyppi.toLowerCase() === "rekisterointitoimipaikka") {
                    rekisterointiToimipaikkaUpdatedText = value.julkaisuaika
                }

                apiData.push(new ApiDataModel(value.id.toString(), true, value.koordinaatit, value, null, null))
            })

            // Rakennetaan tiedot päivitetty teksti
            const rightSlotData = new DynamicRightSlotData(data[0].id.toString())
            if (lupaToimipaikkaUpdatedText.length > 0 && rekisterointiToimipaikkaUpdatedText.length > 0) {
                rightSlotData.dataUpdatedDate
                    = this.getUpdatedtext(lupaToimipaikkaUpdatedText) + " (" + t("App.TieLiikenne.AjokorttiJaLupapalvelupisteet") + ")<br>"
                    + this.getUpdatedtext(rekisterointiToimipaikkaUpdatedText) + " (" + t("App.TieLiikenne.AjoneuvojenRekisteröintitoimipaikat") + ")"
            }
            else if (lupaToimipaikkaUpdatedText.length > 0) {
                rightSlotData.dataUpdatedDate = this.getUpdatedtext(lupaToimipaikkaUpdatedText)
            }
            else if (rekisterointiToimipaikkaUpdatedText.length > 0) {
                rightSlotData.dataUpdatedDate = this.getUpdatedtext(rekisterointiToimipaikkaUpdatedText)
            }
            store.dispatch("setRightSlotData", rightSlotData)

            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    async handleDataLiikenneonnettomuudet(data): Promise<ApiDataModel[]> {
        try {
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData ja päivitetään kartalla näytettävien alueiden värit
            data.forEach((value) => {
                let id: string = "0"
                let color = ""

                // Lasketaan alueen väri datan perusteella
                if (areaType === AreaTypes.Municipality) {
                    color = this.getLiikenneonnettomuudetLegendColorMunicipalities(value.lukumaarakuolleet + value.lukumaaraloukkaantuneet)
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    color = this.getLiikenneonnettomuudetLegendColorProvince(value.lukumaarakuolleet + value.lukumaaraloukkaantuneet)
                    id = value.maakuntanumero ?? "0"
                }

                const julkaisuVuosi = this.LiikenneUtils.getJulkaisuVuosi(data)
                const tableValues
                    = [[(julkaisuVuosi - 1).toString(), value.lukumaarakuolleet.toString(), value.lukumaaraloukkaantuneet.toString()],
                    [(julkaisuVuosi - 2).toString(), value.lukumaarakuolleetedellinen.toString(), value.lukumaaraloukkaantuneetedellinen.toString()]]

                const rightSlotData = new DynamicRightSlotData(id)
                rightSlotData.tableData = new TableData(this.liikenneonnettomuudetTableColumHeaders, tableValues, null)
                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.TieLiikenne.EntireFinland") ?? ""
                }
                else {
                    rightSlotData.municipality = this.LiikenneUtils.getName(value)
                }

                if (areaType === AreaTypes.Country) {
                    store.dispatch("setRightSlotData", rightSlotData)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id, "")
            })
            return models
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    //
    // Käsitellään käyttövoimittain lajitellut tieliikenteen tilastot
    //
    async handleDataLiikenteenTilastotKayttovoimittain(type: string, data): Promise<ApiDataModel[]> {
        try {
            this.LiikenneUtils.updateJulkaisuvuosi(data)
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData ja päivitetään kartalla näytettävien alueiden värit
            data.forEach((value) => {
                let id: string = "0"
                // Kartalla näytettävä väri
                let color = ""
                switch (type) {
                    case "EnsirekisteroidytHenkiloAutot":
                        color = this.getEnsirekisteroidytHenkiloAutotLegendColor(value.suhteellinen, value.kayttovoima)
                        break
                    case "LiikennekaytossaHenkiloAutot":
                        color = this.getLiikennekaytossaHenkiloAutotLegendColor(value.suhteellinen, value.kayttovoima)
                        break
                }
                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)
                rightSlotData.dataUpdatedDate = this.getUpdatedtext(value.julkaisuaika ?? moment().toISOString()) ?? "?"
                // Kunnan ja maakunnan rightslotissa näytettävä kaikkien käyttövoimien tilastot haetaan vasta kun kunta/maakunta valitaan kartalta
                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.TieLiikenne.EntireFinland") ?? ""
                }
                else {
                    rightSlotData.municipality = this.LiikenneUtils.getName(value)
                }

                if (areaType === AreaTypes.Country) {
                    // Koko maan kaikkien käyttövoimien tilastot tulevat ensimmäisen haun mukana ja näytetään heti rightslotissa
                    const tableValues: Array<Array<string>> = []
                    value.tilastot?.forEach((tilasto) => {
                        tableValues.push([
                            t(tilasto.kayttovoimakielitermi) ?? "",
                            tilasto.lukumaara?.toString(),
                            tilasto.suhteellinen != null ? tilasto.suhteellinen?.toString() + "%" : "",
                            tilasto.erotusprosentuaalinen != null ? tilasto.erotusprosentuaalinen?.toString() + "%" : ""
                        ])
                    })
                    rightSlotData.tableData = new TableData(this.henkiloautotCountsTableColumnHeaders, tableValues, null)
                    store.dispatch("setRightSlotData", rightSlotData)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id, value.kayttovoima)
            })
            return models
        }
        catch (err) {
            console.log(err)
            return new Array<ApiDataModel>()
        }
    }

    async handleDataEnsirekisteroidytHenkiloAutotPaastot(data): Promise<ApiDataModel[]> {
        try {
            this.LiikenneUtils.updateJulkaisuvuosi(data)
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData ja päivitetään kartalla näytettävien alueiden värit
            data.forEach((value) => {
                let id: string = "0"
                const color = this.getEnsirekisteroidytHenkiloAutotPaastotLegendColor(value.lukumaara)
                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)

                // Kunnan ja maakunnan kaikkien käyttövoimien tilastot haetaan vasta kun kunta/maakunta valitaan kartalta
                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.TieLiikenne.EntireFinland") ?? ""
                }
                else {
                    rightSlotData.municipality = this.LiikenneUtils.getName(value)
                }

                if (areaType === AreaTypes.Country) {
                    // Koko maan tilastot tulevat ensimmäisen haun mukana
                    const tableValues: Array<Array<string>> = []
                    tableValues.push([
                        value.lukumaara?.toString(),
                        value.erotusprosentuaalinen != null ? value.erotusprosentuaalinen?.toString() + "%" : ""
                    ])
                    rightSlotData.tableData = new TableData(this.ensirekisteroityjenHenkiloAutojenPaastotTableColumnHeaders, tableValues, null)
                    store.dispatch("setRightSlotData", rightSlotData)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id, "")
            })
            return models
        }
        catch (err) {
            console.log(err)
            return new Array<ApiDataModel>()
        }
    }

    async handleDataLiikennekaytossaHenkiloAutotKeskiIka(data): Promise<ApiDataModel[]> {
        try {
            this.LiikenneUtils.updateJulkaisuvuosi(data)
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData ja päivitetään kartalla näytettävien alueiden värit
            data.forEach((value) => {
                let id: string = "0"
                // Kartalla näytettävä väri
                const color = this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendColor(value.lukumaara)
                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)

                // Kunnan ja maakunnan rightslotissa näytettävä tilasto haetaan vasta kun kunta/maakunta valitaan kartalta
                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.TieLiikenne.EntireFinland") ?? ""
                }
                else {
                    rightSlotData.municipality = this.LiikenneUtils.getName(value)
                }

                if (areaType === AreaTypes.Country) {
                    // Koko maan tilasto tulee ensimmäisen haun mukana ja näytetään heti rightslotissa
                    const tableValues: Array<Array<string>> = []
                    tableValues.push([
                        value.lukumaara?.toString(),
                        value.erotusprosentuaalinen != null ? value.erotusprosentuaalinen?.toString() + "%" : ""
                    ])
                    rightSlotData.tableData = new TableData(this.liikennekaytossaHenkiloAutotKeskiIkaTableColumnHeaders, tableValues, null)
                    store.dispatch("setRightSlotData", rightSlotData)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id, "")
            })
            return models
        }
        catch (err) {
            console.log(err)
            return new Array<ApiDataModel>()
        }
    }

    async handleDataMyonnetytTaksiluvat(data): Promise<ApiDataModel[]> {
        try {
            this.LiikenneUtils.updateJulkaisuvuosi(data)
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData ja päivitetään kartalla näytettävien alueiden värit
            data.forEach((value) => {
                let id: string = "0"
                const color = this.getMyonnetytTaksiluvatLegendColor(value.suhteellinen)
                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)

                const tableValues: Array<Array<string>> = []
                tableValues.push([
                    value.lukumaara?.toString(),
                    value.erotusprosentuaalinen != null ? value.erotusprosentuaalinen?.toString() + "%" : ""
                ])
                rightSlotData.tableData = new TableData(this.myonnetytTaksiluvatTableColumnHeaders, tableValues, null)

                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.TieLiikenne.EntireFinland") ?? ""
                    store.dispatch("setRightSlotData", rightSlotData)
                }
                else {
                    rightSlotData.municipality = this.LiikenneUtils.getName(value)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id, "")
            })
            return models
        }
        catch (err) {
            console.log(err)
            return new Array<ApiDataModel>()
        }
    }

    async handleDataVoimassaOlevatAjokortit(data): Promise<ApiDataModel[]> {
        try {
            this.LiikenneUtils.updateJulkaisuvuosi(data)
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData ja päivitetään kartalla näytettävien alueiden värit
            data.forEach((value) => {
                let id: string = "0"
                const color = this.getVoimassaOlevatAjokortitLegendColor(value.suhteellinen)
                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)

                const tableValues: Array<Array<string>> = []
                    tableValues.push([
                        value.lukumaara?.toString(),
                        value.erotusprosentuaalinen != null ? value.erotusprosentuaalinen?.toString() + "%" : ""
                    ])
                rightSlotData.tableData = new TableData(this.voimassaOlevatAjokortitTableColumnHeaders, tableValues, null)

                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.TieLiikenne.EntireFinland") ?? ""
                    store.dispatch("setRightSlotData", rightSlotData)
                }
                else {
                    rightSlotData.municipality = this.LiikenneUtils.getName(value)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id, "")
            })
            return models
        }
        catch (err) {
            console.log(err)
            return new Array<ApiDataModel>()
        }
    }

    getExternalLink(): string {
        return store.getters.contentServiceUrl + "/Content/icons/svg-blue/linkki-external.svg" + "?" + store.getters.contentServiceTimeStamp
    }

    getExternalLinkAlt(): string | undefined {
        return t("App.Link.UlosJohtavaLinkki")
    }

    updateModels(models: ApiDataModel[], rightSlotData: DynamicRightSlotData, color: string, id: string, kayttovoima: string) {
        const item = models.find(e => e.id === id)
        if (item !== undefined) {
            item.rightSlotData = rightSlotData
            item.featureData.kayttovoima = kayttovoima
            item.featureData.koordinaatit.options.fillColor = color
            item.featureData.koordinaatit.options.fillOpacity = color === "" ? 0 : 0.8 // jos ei ole väriä niin täysi läpinäkyvyys
            if (item.geometryData !== null) {
                // Vain ne alueet voi valita kartalta, joilla on dataa
                item.geometryData.handleOnClick = true
            }
        }
    }

    handleHenkiloAutotCountsRightslotData(id: string, data, municipality: string): DynamicRightSlotData {
        const rightSlotData = new DynamicRightSlotData(id)
        const tableValues: Array<Array<string>> = []

        data.forEach((tilasto) => {
            tableValues.push([
                t(tilasto.kayttovoimakielitermi) ?? "",
                tilasto.lukumaara?.toString(),
                tilasto.suhteellinen != null ? tilasto.suhteellinen?.toString() + "%" : "",
                tilasto.erotusprosentuaalinen != null ? tilasto.erotusprosentuaalinen?.toString() + "%" : ""
            ])
        })

        rightSlotData.tableData = new TableData(this.henkiloautotCountsTableColumnHeaders, tableValues, null)
        rightSlotData.municipality = municipality

        return rightSlotData
    }

    handleEnsirekisteroidytHenkiloAutotPaastotRightslotData(id: string, data, updatedText: string, municipality: string): DynamicRightSlotData {
        const rightSlotData = new DynamicRightSlotData(id)
        const tableValues: Array<Array<string>> = []

        data.forEach((tilasto) => {
            tableValues.push([
                tilasto.lukumaara?.toString(),
                tilasto.erotusprosentuaalinen != null ? tilasto.erotusprosentuaalinen?.toString() + "%" : ""
            ])
        })
        rightSlotData.tableData = new TableData(this.ensirekisteroityjenHenkiloAutojenPaastotTableColumnHeaders, tableValues, null)
        rightSlotData.dataUpdatedDate = updatedText
        rightSlotData.municipality = municipality

        return rightSlotData
    }

    handleLiikennekaytossaHenkiloAutotKeskiIkaRightslotData(id: string, data, updatedText: string, municipality: string): DynamicRightSlotData {
        const rightSlotData = new DynamicRightSlotData(id)
        const tableValues: Array<Array<string>> = []

        data.forEach((tilasto) => {
            tableValues.push([
                tilasto.lukumaara?.toString(),
                tilasto.erotusprosentuaalinen != null ? tilasto.erotusprosentuaalinen?.toString() + "%" : ""
            ])
        })
        rightSlotData.tableData = new TableData(this.liikennekaytossaHenkiloAutotKeskiIkaTableColumnHeaders, tableValues, null)
        rightSlotData.dataUpdatedDate = updatedText
        rightSlotData.municipality = municipality

        return rightSlotData
    }

    handleMyonnetytTaksiluvatRightslotData(id: string, data: string, updatedText: string, municipality: string): DynamicRightSlotData {
        const rightSlotData = new DynamicRightSlotData(id)
        const tableValues: Array<Array<string>> = []
        const json = JSON.parse(data)
        json.forEach((tilasto) => {
            tableValues.push([
                tilasto.lukumaara?.toString(),
                tilasto.erotusprosentuaalinen != null ? tilasto.erotusprosentuaalinen?.toString() + "%" : ""
            ])
        })
        rightSlotData.tableData = new TableData(this.myonnetytTaksiluvatTableColumnHeaders, tableValues, null)
        rightSlotData.dataUpdatedDate = updatedText
        rightSlotData.municipality = municipality

        return rightSlotData
    }

    handleVoimassaOlevatAjokortitRightslotData(id: string, data: string, updatedText: string, municipality: string): DynamicRightSlotData {
        const rightSlotData = new DynamicRightSlotData(id)
        const tableValues: Array<Array<string>> = []
        const json = JSON.parse(data)
        json.forEach((tilasto) => {
            tableValues.push([
                tilasto.lukumaara?.toString(),
                tilasto.erotusprosentuaalinen != null ? tilasto.erotusprosentuaalinen?.toString() + "%" : ""
            ])
        })
        rightSlotData.tableData = new TableData(this.voimassaOlevatAjokortitTableColumnHeaders, tableValues, null)
        rightSlotData.dataUpdatedDate = updatedText
        rightSlotData.municipality = municipality

        return rightSlotData
    }

    getUpdatedtext(time: string): string {
        const timestring = moment(time).format("MM/YYYY") ?? "?"
        return t("App.TieLiikenne.DataUpdated") + " " + timestring
    }

    getToimipaikkaTyyppiIcon(tyyppi: string) {
        if (tyyppi.toLowerCase() === "ajokorttijalupapalvelupiste") {
            return this.MarkerUtils.createTurquoiseMarker()
        }
        else {
            return this.MarkerUtils.createPurpleMarker()
        }
    }

    getToimipaikkaTyyppiLegend(): string {
        const text1 = t("App.TieLiikenne.AjokorttiJaLupapalvelupisteet") ?? ""
        const text2 = t("App.TieLiikenne.AjoneuvojenRekisteröintitoimipaikat") ?? ""
        let legend = "<div class=\"legend-item\"><div><img alt=\"\" src= " + this.getToimipaikkaTyyppiIcon("ajokorttijalupapalvelupiste") + "\"></div><div>" + text1 + "</div></div>"
        legend += "<div class=\"legend-item\"><div><img alt=\"\" src= " + this.getToimipaikkaTyyppiIcon("") + "\"></div><div>" + text2 + "</div></div>"

        return legend
    }

    getLiikenneonnettomuudetLegendProvinces(): string {
        let legend = "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorProvince(-1) + "\"></i></div><div><div>" + t("App.TieLiikenne.Legend.TietoEiSaatavilla") + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorProvince(50) + "\"></i></div><div><div>" + "<100" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorProvince(120) + "\"></i></div><div><div>" + "100-150" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorProvince(170) + "\"></i></div><div><div>" + "150-200" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorProvince(220) + "\"></i></div><div><div>" + "200-250" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorProvince(270) + "\"></i></div><div><div>" + "250-300" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorProvince(350) + "\"></i></div><div><div>" + ">300" + "</div></div></div>"

        return legend
    }

    getLiikenneonnettomuudetLegendMunicipalities(): string {
        let legend = "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorMunicipalities(-1) + "\"></i></div><div><div>" + t("App.TieLiikenne.Legend.TietoEiSaatavilla") + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorMunicipalities(2) + "\"></i></div><div><div>" + "<5" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorMunicipalities(7) + "\"></i></div><div><div>" + "5-10" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorMunicipalities(12) + "\"></i></div><div><div>" + "10-15" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorMunicipalities(17) + "\"></i></div><div><div>" + "15-20" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorMunicipalities(22) + "\"></i></div><div><div>" + "20-25" + "</div></div></div>"
        legend += "<div class=\"legend-item\"><div><i style=\"background: " + this.getLiikenneonnettomuudetLegendColorMunicipalities(27) + "\"></i></div><div><div>" + ">25" + "</div></div></div>"

        return legend
    }

    getEnsirekisteroidytHenkiloAutotLegend(kayttovoima: string): string {
        const limits = this.getEnsirekisteroidytHenkiloAutotLegendLimits(kayttovoima)
        if (limits.length === 0) {
            return ""
        }

        let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotLegendColor(-1, kayttovoima) + "\"></i><div>" + t("App.TieLiikenne.Legend.TietoEiSaatavilla") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotLegendColor(limits[0] - 1, kayttovoima) + "\"></i><div>" + "<" + limits[0] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotLegendColor(limits[1], kayttovoima) + "\"></i><div>" + limits[0] + "-" + limits[1] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotLegendColor(limits[2], kayttovoima) + "\"></i><div>" + limits[1] + "-" + limits[2] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotLegendColor(limits[3], kayttovoima) + "\"></i><div>" + limits[2] + "-" + limits[3] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotLegendColor(limits[4], kayttovoima) + "\"></i><div>" + limits[3] + "-" + limits[4] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotLegendColor(limits[5], kayttovoima) + "\"></i><div>" + ">" + limits[4] + " %" + "</div></div>"

        return legend
    }

    getEnsirekisteroidytHenkiloAutotPaastotLegend(): string {
        let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotPaastotLegendColor(-1) + "\"></i><div>" + t("App.TieLiikenne.Legend.TietoEiSaatavilla") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotPaastotLegendColor(50) + "\"></i><div>" + "< 100 CO2 g/km" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotPaastotLegendColor(105) + "\"></i><div>" + "100 - 110 CO2 g/km" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotPaastotLegendColor(115) + "\"></i><div>" + "110 - 120 CO2 g/km" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotPaastotLegendColor(125) + "\"></i><div>" + "120 - 130 CO2 g/km" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotPaastotLegendColor(135) + "\"></i><div>" + "130 - 140 CO2 g/km" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getEnsirekisteroidytHenkiloAutotPaastotLegendColor(145) + "\"></i><div>" + "> 140 CO2 g/km" + "</div></div>"

        return legend
    }

    getLiikennekaytossaHenkiloAutotLegend(kayttovoima: string): string {
        const limits = this.getLiikennekaytossaHenkiloAutotLegendLimits(kayttovoima)
        if (limits.length === 0) {
            return ""
        }

        let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotLegendColor(-1, kayttovoima) + "\"></i><div>" + t("App.TieLiikenne.Legend.TietoEiSaatavilla") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotLegendColor(limits[0] - 1, kayttovoima) + "\"></i><div>" + "<" + limits[0] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotLegendColor(limits[1], kayttovoima) + "\"></i><div>" + limits[0] + "-" + limits[1] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotLegendColor(limits[2], kayttovoima) + "\"></i><div>" + limits[1] + "-" + limits[2] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotLegendColor(limits[3], kayttovoima) + "\"></i><div>" + limits[2] + "-" + limits[3] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotLegendColor(limits[4], kayttovoima) + "\"></i><div>" + limits[3] + "-" + limits[4] + " %" + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotLegendColor(limits[5], kayttovoima) + "\"></i><div>" + ">" + limits[4] + " %" + "</div></div>"

        return legend
    }

    getLiikennekaytossaHenkiloAutotKeskiIkaLegend(): string {
        const limits = this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendLimits()
        if (limits.length === 0) {
            return ""
        }

        const years = t("App.Legend.Vuotta") ?? ""
        let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendColor(-1) + "\"></i><div>" + t("App.TieLiikenne.Legend.TietoEiSaatavilla") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendColor(limits[0] - 1) + "\"></i><div>" + "<" + limits[0] + " " + years + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendColor(limits[1]) + "\"></i><div>" + limits[0] + "-" + limits[1] + " " + years + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendColor(limits[2]) + "\"></i><div>" + limits[1] + "-" + limits[2] + " " + years + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendColor(limits[3]) + "\"></i><div>" + limits[2] + "-" + limits[3] + " " + years + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendColor(limits[4]) + "\"></i><div>" + limits[3] + "-" + limits[4] + " " + years + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendColor(limits[5]) + "\"></i><div>" + ">" + limits[4] + " " + years + "</div></div>"

        return legend
    }

    getMyonnetytTaksiluvatLegend(): string {
        const limits = this.myonnetytTaksiluvatLegendLimits
        if (limits.length === 0) {
            return ""
        }

        const unit = t("App.Legend.TaksiluvatUnit") ?? ""
        let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getMyonnetytTaksiluvatLegendColor(-1) + "\"></i><div>" + t("App.TieLiikenne.Legend.TietoEiSaatavilla") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMyonnetytTaksiluvatLegendColor(limits[0] - 1) + "\"></i><div>" + "<" + limits[0] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMyonnetytTaksiluvatLegendColor(limits[1]) + "\"></i><div>" + limits[0] + "-" + limits[1] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMyonnetytTaksiluvatLegendColor(limits[2]) + "\"></i><div>" + limits[1] + "-" + limits[2] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMyonnetytTaksiluvatLegendColor(limits[3]) + "\"></i><div>" + limits[2] + "-" + limits[3] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMyonnetytTaksiluvatLegendColor(limits[4]) + "\"></i><div>" + limits[3] + "-" + limits[4] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMyonnetytTaksiluvatLegendColor(limits[4] + 1) + "\"></i><div>" + ">" + limits[4] + " " + unit + "</div></div>"

        return legend
    }

    getVoimassaOlevatAjokortitLegend(): string {
        const limits = this.voimassaOlevatAjokortitLegendLimits
        if (limits.length === 0) {
            return ""
        }

        const unit = t("App.Legend.VoimassaOlevatAjokortitUnit") ?? ""
        let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getVoimassaOlevatAjokortitLegendColor(-1) + "\"></i><div>" + t("App.TieLiikenne.Legend.TietoEiSaatavilla") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVoimassaOlevatAjokortitLegendColor(limits[0] - 1) + "\"></i><div>" + "<" + limits[0] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVoimassaOlevatAjokortitLegendColor(limits[1]) + "\"></i><div>" + limits[0] + "-" + limits[1] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVoimassaOlevatAjokortitLegendColor(limits[2]) + "\"></i><div>" + limits[1] + "-" + limits[2] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVoimassaOlevatAjokortitLegendColor(limits[3]) + "\"></i><div>" + limits[2] + "-" + limits[3] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVoimassaOlevatAjokortitLegendColor(limits[4]) + "\"></i><div>" + limits[3] + "-" + limits[4] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getVoimassaOlevatAjokortitLegendColor(limits[4] + 1) + "\"></i><div>" + ">" + limits[4] + " " + unit + "</div></div>"

        return legend
    }

    getEnsirekisteroidytHenkiloAutotLegendLimits(kayttovoima: string): Array<number> {
        if (kayttovoima != null) {
            kayttovoima = kayttovoima.toLowerCase()

            if (kayttovoima === "bensiini") {
                return [66, 68, 70, 72, 74, 100]
            }
            else if (kayttovoima === "diesel") {
                return [10, 12, 14, 16, 18, 100]
            }
            else if (kayttovoima === "kaasu") {
                return [1, 3, 5, 7, 9, 100]
            }
            else if (kayttovoima === "ladattavat hybridit") {
                return [6, 8, 10, 12, 14, 100]
            }
            else if (kayttovoima === "sähkö") {
                return [1, 3, 5, 7, 9, 100]
            }
            else if (kayttovoima === "muut") {
                return [0.1, 0.3, 0.5, 0.7, 0.9, 100]
            }
        }
        return []
    }

    getLiikennekaytossaHenkiloAutotLegendLimits(kayttovoima: string): Array<number> {
        if (kayttovoima != null) {
            kayttovoima = kayttovoima.toLowerCase()

            if (kayttovoima === "bensiini") {
                return [66, 68, 70, 72, 74, 100]
            }
            else if (kayttovoima === "diesel") {
                return [25, 30, 35, 40, 45, 100]
            }
            else if (
                kayttovoima === "kaasu"
                || kayttovoima === "ladattavat hybridit"
                || kayttovoima === "sähkö"
                || kayttovoima === "muut") {
                return [0.1, 0.3, 0.6, 0.9, 1.2, 100]
            }
        }
        return []
    }

    getLiikenneonnettomuudetLegendLimitsMunicipalities(): Array<number> {
        return [5, 10, 15, 20, 25, 100]
    }

    getLiikenneonnettomuudetLegendLimitsProvinces(): Array<number> {
        return [100, 150, 200, 250, 300, 1000]
    }

    getEnsirekisteroidytHenkiloAutotPaastotLegendLimits(): Array<number> {
        return [100, 110, 120, 130, 140, 1000]
    }

    getLiikennekaytossaHenkiloAutotKeskiIkaLegendLimits(): Array<number> {
        return [10, 11, 12, 13, 14, 100]
    }

    getEnsirekisteroidytHenkiloAutotLegendColor(value: number, kayttovoima: string): string {
        const limits = this.getEnsirekisteroidytHenkiloAutotLegendLimits(kayttovoima)
        return this.LiikenneUtils.getLegendColor(value, limits)
    }

    getLiikennekaytossaHenkiloAutotLegendColor(value: number, kayttovoima: string): string {
        const limits = this.getLiikennekaytossaHenkiloAutotLegendLimits(kayttovoima)
        return this.LiikenneUtils.getLegendColor(value, limits)
    }

    getLiikennekaytossaHenkiloAutotKeskiIkaLegendColor(value: number): string {
        const limits = this.getLiikennekaytossaHenkiloAutotKeskiIkaLegendLimits()
        return this.LiikenneUtils.getLegendColor(value, limits)
    }

    getEnsirekisteroidytHenkiloAutotPaastotLegendColor(value: number): string {
        const limits = this.getEnsirekisteroidytHenkiloAutotPaastotLegendLimits()
        return this.LiikenneUtils.getLegendColor(value, limits)
    }

    getLiikenneonnettomuudetLegendColorMunicipalities(value: number): string {
        const limits = this.getLiikenneonnettomuudetLegendLimitsMunicipalities()
        return this.LiikenneUtils.getLegendColor(value, limits)
    }

    getLiikenneonnettomuudetLegendColorProvince(value: number): string {
        const limits = this.getLiikenneonnettomuudetLegendLimitsProvinces()
        return this.LiikenneUtils.getLegendColor(value, limits)
    }

    getMyonnetytTaksiluvatLegendColor(value: number): string {
        return this.LiikenneUtils.getLegendColor(value, this.myonnetytTaksiluvatLegendLimits)
    }

    getVoimassaOlevatAjokortitLegendColor(value: number): string {
        return this.LiikenneUtils.getLegendColor(value, this.voimassaOlevatAjokortitLegendLimits)
    }

    get liikenneonnettomuudetTableColumHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.TieLiikenne.Vuosi") ?? "App.TieLiikenne.Vuosi")
        columns.push(t("App.TieLiikenne.Kuolleet") ?? "App.TieLiikenne.Kuolleet")
        columns.push(t("App.TieLiikenne.Loukkaantuneet") ?? "App.TieLiikenne.Loukkaantuneet")
        return columns
    }

    get henkiloautotCountsTableColumnHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.Tieliikenne.RightSlot.Kayttovoima") ?? "App.Tieliikenne.RightSlot.Kayttovoima")
        columns.push(t("App.Tieliikenne.RightSlot.Maara") ?? "App.Tieliikenne.RightSlot.Maara")
        columns.push(t("App.Tieliikenne.RightSlot.Osuus") ?? "App.Tieliikenne.RightSlot.Osuus")
        columns.push(t("App.Tieliikenne.RightSlot.Vuosimuutos") ?? "App.Tieliikenne.RightSlot.Vuosimuutos")
        return columns
    }

    get liikennekaytossaHenkiloAutotKeskiIkaTableColumnHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.Tieliikenne.RightSlot.Vuotta") ?? "App.Tieliikenne.RightSlot.Vuotta")
        columns.push(t("App.Tieliikenne.RightSlot.Vuosimuutos") ?? "App.Tieliikenne.RightSlot.Vuosimuutos")
        return columns
    }

    get ensirekisteroityjenHenkiloAutojenPaastotTableColumnHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.Tieliikenne.RightSlot.CO2Paastoarvo") ?? "App.Tieliikenne.RightSlot.CO2Paastoarvo")
        columns.push(t("App.Tieliikenne.RightSlot.Vuosimuutos") ?? "App.Tieliikenne.RightSlot.Vuosimuutos")
        return columns
    }

    get myonnetytTaksiluvatTableColumnHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.Tieliikenne.RightSlot.Maara") ?? "App.Tieliikenne.RightSlot.Maara")
        columns.push(t("App.Tieliikenne.RightSlot.Vuosimuutos") ?? "App.Tieliikenne.RightSlot.Vuosimuutos")
        return columns
    }

    get voimassaOlevatAjokortitTableColumnHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.Tieliikenne.RightSlot.Maara") ?? "App.Tieliikenne.RightSlot.Maara")
        columns.push(t("App.Tieliikenne.RightSlot.Vuosimuutos") ?? "App.Tieliikenne.RightSlot.Vuosimuutos")
        return columns
    }

    createPopUpLinkAriaLabel(name: string) {
        return t("App.Tieliikenne.Popup.PalvelutHinnastoJaAukioloajat") + " " + t("App.Tieliikenne.Popup.PalvelupisteenVerkkosivuilta") + " " + name
    }
}

export interface LupaJaRekisterointitoimipaikat {
    id: number;
    tyyppi: string;
    julkaisuaika: string;
    nimi: string;
    katuosoite: string | null;
    postinro: string | null;
    postitoimipaikka: string | null;
    info: string | null;
    infosv: string | null;
    infoen: string | null;
    urlfi: string | null;
    urlsv: string | null;
    urlen: string | null;

    koordinaatit: GeometryData;
    popup: string;
}
